import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { modalState } from '@/state/modal.state';
import { useUserAccount } from '@/user/use-user-account';

import {
  mapNewUsersToPayload,
  mapTeamIdsToPayload,
  mapUserIdsToPayload,
} from '../helpers/map-share-payload';
import { ShareModal } from '../share-modal';
import type { ShareWithData } from '../share-with-select/share-with';

import { useShareCompany } from './use-share-company';
import { useUsersAndTeamsWithAccessToOrganization } from './use-users-and-teams-with-access-to-organization';
import { useAtomValue } from 'jotai';

export const ShareCompanyModal = () => {
  const { t } = useTranslation('default');

  const modal = useAtomValue(modalState);

  const organizationId =
    modal?.state === 'shareCompany' ? modal.companyId : null;

  const { shareCompany } = useShareCompany();
  const { isLoading, usersAndTeamsWithAccessToOrganization } =
    useUsersAndTeamsWithAccessToOrganization(organizationId);

  const userAccount = useUserAccount();

  const [shareWithData, setShareWithData] = useState<ShareWithData>({
    draftOptions: [],
    removedOptions: [],
    isPublic: null,
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setShareWithData(prevState => ({
      ...prevState,
      draftOptions: usersAndTeamsWithAccessToOrganization,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const handleSubmit = async () => {
    if (!organizationId) return;

    const { draftOptions } = shareWithData;

    await shareCompany({
      newUsers: mapNewUsersToPayload(draftOptions),
      teamIds: mapTeamIdsToPayload(draftOptions),
      userIds: mapUserIdsToPayload(draftOptions),
      organizationId,
    });
  };

  return (
    <ShareModal
      shareWithData={shareWithData}
      entitiesWithAccess={usersAndTeamsWithAccessToOrganization}
      title={t`share.shareCompany`}
      isLoading={isLoading}
      privacyPublicLabel={
        <Trans
          ns="default"
          i18nKey="share.companyPublicLabel"
          values={{ accountName: userAccount?.name }}
          components={{ u: <u /> }}
        />
      }
      onChange={setShareWithData}
      onSubmit={handleSubmit}
    />
  );
};
